import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { X, Crown, Heart, Sparkles } from "lucide-react";
import { vibrate } from "@/lib/api";

function healthColor(s) {
  if (s == null) return "#71717a";
  return s < 40 ? "#ef4444" : s < 70 ? "#f59e0b" : "#10b981";
}

// `data` is the enriched 402 payload: { quota_type, current, limit, tier,
// upgrade_tier, upgrade_price, context:{relationship_name, health_score}, upgrade_url }
export default function PaywallModal({ data, onClose }) {
  const navigate = useNavigate();
  if (!data) return null;

  const ctx = data.context || {};
  const name = ctx.relationship_name;
  const hs = ctx.health_score;
  const isRelations = data.quota_type === "relations";
  const tierName = { basic: "Basic", pro: "Pro", docteur: "Docteur" }[data.upgrade_tier] || "Premium";

  const title = isRelations
    ? name && name !== "Relation sans nom"
      ? `Continue de suivre ${name}`
      : "Débloque le suivi illimité"
    : "Tu as utilisé tes analyses gratuites";

  const subtitle = isRelations
    ? "Ton plan actuel ne suit qu'une seule relation. Passe Pro pour suivre toutes tes histoires en même temps."
    : `Tu as fait ${data.current}/${data.limit} analyses. Passe ${tierName} pour des analyses illimitées et garder un œil sur ${name && name !== "Relation sans nom" ? name : "tes relations"}.`;

  const goUpgrade = () => {
    vibrate([100, 50, 100]);
    onClose?.();
    navigate(data.upgrade_url || "/pricing");
  };

  return (
    <AnimatePresence>
      <motion.div
        data-testid="paywall-modal"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/85 backdrop-blur-sm px-4 pb-4 sm:pb-0"
        onClick={onClose}
      >
        <motion.div
          initial={{ y: 40, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 40, opacity: 0 }}
          transition={{ type: "spring", damping: 26, stiffness: 300 }}
          onClick={(e) => e.stopPropagation()}
          className="relative w-full max-w-sm rounded-3xl bg-zinc-900 border border-pink-500/30 p-6 overflow-hidden"
        >
          <div
            className="absolute -top-24 -right-16 w-56 h-56 rounded-full opacity-25 blur-3xl"
            style={{ background: "#ff007f" }}
          />
          <button
            data-testid="paywall-close"
            onClick={onClose}
            className="absolute top-4 right-4 text-zinc-500 hover:text-white z-10"
          >
            <X size={20} />
          </button>

          <div className="relative">
            <div className="w-11 h-11 rounded-2xl bg-pink-500/15 flex items-center justify-center text-pink-400 mb-4">
              {isRelations ? <Heart size={22} /> : <Sparkles size={22} />}
            </div>

            <h2 data-testid="paywall-title" className="font-display text-2xl font-black tracking-tight leading-tight">
              {title}
            </h2>
            <p className="text-sm text-zinc-400 mt-2">{subtitle}</p>

            {hs != null && (
              <div className="mt-5 flex items-center gap-4 rounded-2xl bg-zinc-950 border border-zinc-800 p-4">
                <div className="text-center">
                  <span className="font-display text-4xl font-black" style={{ color: healthColor(hs) }}>{hs}</span>
                  <p className="text-[9px] uppercase tracking-wider text-zinc-600">santé</p>
                </div>
                <p className="text-xs text-zinc-400 flex-1">
                  <span className="text-white font-semibold">Ne perds pas le fil.</span> {name} évolue — reste alerté avant qu'il ne soit trop tard.
                </p>
              </div>
            )}

            <button
              data-testid="paywall-upgrade"
              onClick={goUpgrade}
              className="mt-6 w-full flex items-center justify-center gap-2 rounded-full bg-pink-500 text-white font-bold py-4 hover:bg-pink-600 transition-colors glow-pink active:scale-95"
            >
              <Crown size={18} /> Passer {tierName}
              {data.upgrade_price && <span className="opacity-80">· {data.upgrade_price}/mois</span>}
            </button>
            <button
              data-testid="paywall-later"
              onClick={onClose}
              className="mt-2 w-full text-sm text-zinc-500 hover:text-zinc-300 py-2"
            >
              Plus tard
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
